
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PostCard } from "@/components/post/PostCard";
import { EmptyState } from "@/components/ui/EmptyState";
import { Spinner } from "@/components/ui/Spinner";
import { Tabs } from "@/components/ui/Tabs";
import { IconComment, IconBookmark, IconBarChart } from "@/components/ui/Icons";
import type { Post } from "@/types";

interface ProfileTabsProps {
  profileId: string;
  username: string;
  isOwnProfile: boolean;
  currentUserId: string | null;
}

type ProfileTab = "posts" | "comments" | "bookmarks";

export function ProfileTabs({ profileId, username, isOwnProfile, currentUserId }: ProfileTabsProps) {
  const [activeTab, setActiveTab] = useState<ProfileTab>("posts");
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    loadData("posts");
  }, [profileId]);

  const loadData = async (tab: ProfileTab, reset = true) => {
    setLoading(reset);
    try {
      let url = "";
      if (tab === "posts") url = `/api/users/${profileId}/posts?limit=10${reset ? "" : `&cursor=${cursor}`}`;
      else if (tab === "bookmarks" && isOwnProfile) url = `/api/users/${profileId}/bookmarks?limit=10${reset ? "" : `&cursor=${cursor}`}`;
      else return;

      const res = await fetch(url);
      if (!res.ok) return;
      const data = await res.json();
      setPosts(prev => reset ? (data.posts || []) : [...prev, ...(data.posts || [])]);
      setCursor(data.cursor);
      setHasMore(data.has_more);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab: string) => {
    const t = tab as ProfileTab;
    setActiveTab(t);
    setPosts([]);
    setCursor(null);
    setHasMore(true);
    loadData(t, true);
  };

  const tabs = [
    { value: "posts", label: "Posts", icon: <IconBarChart className="w-4 h-4" /> },
    { value: "comments", label: "Comments", icon: <IconComment className="w-4 h-4" />, disabled: true },
    ...(isOwnProfile ? [{ value: "bookmarks", label: "Bookmarks", icon: <IconBookmark className="w-4 h-4" /> }] : []),
  ];

  return (
    <div>
      <Tabs tabs={tabs} value={activeTab} onChange={handleTabChange} variant="line" className="mb-4" />

      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-center py-12">
            <Spinner size="lg" />
          </motion.div>
        ) : posts.length === 0 ? (
          <motion.div key="empty" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <EmptyState
              title={activeTab === "posts" ? "No posts yet" : activeTab === "bookmarks" ? "No bookmarks yet" : "No comments yet"}
              description={activeTab === "posts" ? `${isOwnProfile ? "You haven't" : `@${username} hasn't`} posted anything yet.` : "Nothing saved here yet."}
              icon={activeTab === "bookmarks" ? <IconBookmark className="w-full h-full" /> : <IconBarChart className="w-full h-full" />}
            />
          </motion.div>
        ) : (
          <motion.div key="posts" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            {posts.map((post, i) => (
              <motion.div key={post.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <PostCard post={post} userId={currentUserId} />
              </motion.div>
            ))}
            {hasMore && (
              <button onClick={() => loadData(activeTab, false)}
                className="w-full py-3 text-sm text-brand hover:text-brand-hover font-medium transition-colors">
                Load more
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
